import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Tooltip,
  Chip,
  Typography,
  Modal,
  TextField,
  Stack,
  Autocomplete,
  Alert,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SendIcon from "@mui/icons-material/Send";
import moment from "moment";
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import axios from "../../../config/axiosConfig";
import { useAuth } from "../../../context/AuthContext";

const normalize = (value) =>
  String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toLowerCase();

const FORM_VACIO = {
  id: null,
  numero_documento: "",
  nombre: "",
  descripcion: "",
  fecha_vencimiento: "",
  correos: [],
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const parseCorreos = (texto) =>
  (texto || "")
    .split(",")
    .map((c) => c.trim())
    .filter(Boolean);

const diasRestantes = (fechaVencimiento) => {
  const hoy = moment().startOf("day");
  const venc = moment(fechaVencimiento).startOf("day");
  return venc.diff(hoy, "days");
};

const chipDias = (dias) => {
  if (dias < 0) return { label: `Venció hace ${Math.abs(dias)} día(s)`, color: "default" };
  if (dias <= 5) return { label: `${dias} día(s)`, color: "error" };
  if (dias <= 15) return { label: `${dias} día(s)`, color: "warning" };
  if (dias <= 30) return { label: `${dias} día(s)`, color: "info" };
  return { label: `${dias} día(s)`, color: "success" };
};

const AVISOS = [
  { campo: "aviso_60_enviado", label: "60d" },
  { campo: "aviso_30_enviado", label: "30d" },
  { campo: "aviso_15_enviado", label: "15d" },
  { campo: "aviso_5_enviado", label: "5d" },
];

const VencimientoDocumentosPage = () => {
  const { user } = useAuth();
  const esAdmin = useMemo(() => normalize(user?.role) === "admin", [user?.role]);

  const [documentos, setDocumentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [form, setForm] = useState(FORM_VACIO);
  const [saving, setSaving] = useState(false);

  const [probando, setProbando] = useState(false);
  const [resultadoPrueba, setResultadoPrueba] = useState(null);

  const isEdit = Boolean(form.id);

  const fetchDocumentos = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get("/api/documentos-vencimiento");
      setDocumentos(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error al obtener documentos:", err);
      setError("No se pudieron cargar los documentos.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocumentos();
  }, []);

  const handleOpenCrear = () => {
    setForm(FORM_VACIO);
    setOpenModal(true);
  };

  const handleOpenEditar = (doc) => {
    setForm({
      id: doc.id,
      numero_documento: doc.numero_documento,
      nombre: doc.nombre,
      descripcion: doc.descripcion || "",
      fecha_vencimiento: moment(doc.fecha_vencimiento).format("YYYY-MM-DD"),
      correos: parseCorreos(doc.correos),
    });
    setOpenModal(true);
  };

  const correosInvalidos = form.correos.filter((c) => !EMAIL_REGEX.test(c));

  const handleGuardar = async (e) => {
    e.preventDefault();
    if (!form.correos.length || correosInvalidos.length) return;
    setSaving(true);
    setError("");
    try {
      const payload = {
        numero_documento: form.numero_documento.trim(),
        nombre: form.nombre.trim(),
        descripcion: form.descripcion.trim(),
        fecha_vencimiento: form.fecha_vencimiento,
        correos: form.correos.join(", "),
      };
      if (isEdit) {
        await axios.put(`/api/documentos-vencimiento/${form.id}`, payload);
      } else {
        await axios.post("/api/documentos-vencimiento", payload);
      }
      setOpenModal(false);
      fetchDocumentos();
    } catch (err) {
      console.error("Error al guardar documento:", err);
      setError(err?.response?.data?.error || "No se pudo guardar el documento.");
    } finally {
      setSaving(false);
    }
  };

  const handleEliminar = async (doc) => {
    if (!window.confirm(`¿Eliminar el documento "${doc.nombre}"? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`/api/documentos-vencimiento/${doc.id}`);
      fetchDocumentos();
    } catch (err) {
      console.error("Error al eliminar documento:", err);
      setError("No se pudo eliminar el documento.");
    }
  };

  const handleProbarAvisos = async () => {
    setProbando(true);
    setResultadoPrueba(null);
    try {
      const { data } = await axios.post("/api/documentos-vencimiento/revisar-avisos");
      setResultadoPrueba({ ok: true, ...data });
      fetchDocumentos();
    } catch (err) {
      console.error("Error al probar avisos:", err);
      setResultadoPrueba({ ok: false, error: err?.response?.data?.error || "No se pudo ejecutar la revisión." });
    } finally {
      setProbando(false);
    }
  };

  return (
    <DashboardLayout>
      <PageHeader
        title="Vencimiento de Documentos"
        subtitle="Avisos automáticos por correo a 60/30/15/5 días de vencer"
        actions={
          <Stack direction="row" spacing={1}>
            {esAdmin && (
              <Button
                variant="outlined"
                size="small"
                color="inherit"
                startIcon={probando ? <CircularProgress size={14} color="inherit" /> : <SendIcon />}
                onClick={handleProbarAvisos}
                disabled={probando}
              >
                {probando ? "Revisando..." : "Probar envío de avisos"}
              </Button>
            )}
            <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleOpenCrear}>
              Registrar documento
            </Button>
          </Stack>
        }
      />

      <Box sx={{ p: { xs: 1.5, md: 2 } }}>
        {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

        {resultadoPrueba && (
          <Alert
            severity={resultadoPrueba.ok ? (resultadoPrueba.errores?.length ? "warning" : "success") : "error"}
            onClose={() => setResultadoPrueba(null)}
            sx={{ mb: 2 }}
          >
            {resultadoPrueba.ok ? (
              <>
                Revisados: {resultadoPrueba.revisados} — Avisos enviados: {resultadoPrueba.enviados}
                {resultadoPrueba.errores?.length > 0 && (
                  <Box component="span" sx={{ display: "block", mt: 0.5 }}>
                    Fallaron {resultadoPrueba.errores.length}: {resultadoPrueba.errores.map((e) => `"${e.nombre}" (${e.error})`).join(", ")}
                  </Box>
                )}
              </>
            ) : (
              resultadoPrueba.error
            )}
          </Alert>
        )}

        {!loading && documentos.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No hay documentos registrados todavía.
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>N° Documento</TableCell>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Vence</TableCell>
                  <TableCell>Correos</TableCell>
                  <TableCell>Avisos enviados</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {documentos.map((doc) => {
                  const dias = diasRestantes(doc.fecha_vencimiento);
                  const chip = chipDias(dias);
                  return (
                    <TableRow key={doc.id} hover>
                      <TableCell>{doc.numero_documento}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{doc.nombre}</Typography>
                        {doc.descripcion && (
                          <Typography variant="caption" color="text.secondary">{doc.descripcion}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography variant="caption">{moment(doc.fecha_vencimiento).format("DD/MM/YYYY")}</Typography>
                          <Chip size="small" label={chip.label} color={chip.color} variant="outlined" />
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 220, wordBreak: "break-word" }}>
                        <Typography variant="caption" color="text.secondary">{doc.correos}</Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {AVISOS.map(({ campo, label }) => (
                            <Chip
                              key={campo}
                              size="small"
                              label={label}
                              color={doc[campo] ? "success" : "default"}
                              variant={doc[campo] ? "filled" : "outlined"}
                            />
                          ))}
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => handleOpenEditar(doc)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" onClick={() => handleEliminar(doc)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Paper
          component="form"
          onSubmit={handleGuardar}
          sx={{
            position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
            width: 480, maxWidth: "90vw", p: 3, borderRadius: 2, maxHeight: "90vh", overflowY: "auto",
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            {isEdit ? "Editar documento" : "Registrar documento"}
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="Número de documento"
              value={form.numero_documento}
              onChange={(e) => setForm({ ...form, numero_documento: e.target.value })}
              fullWidth
              autoFocus
              required
            />
            <TextField
              label="Nombre"
              value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Descripción"
              value={form.descripcion}
              onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
              fullWidth
              multiline
              minRows={2}
            />
            <TextField
              label="Fecha de vencimiento"
              type="date"
              value={form.fecha_vencimiento}
              onChange={(e) => setForm({ ...form, fecha_vencimiento: e.target.value })}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
            />
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={form.correos}
              onChange={(e, nuevosValores) => {
                // Autocomplete separa por coma/Enter automáticamente; igual
                // partimos cada entrada por si pegan varios correos de una vez.
                const expandido = nuevosValores.flatMap((v) => parseCorreos(v));
                const sinDuplicados = [...new Set(expandido)];
                setForm({ ...form, correos: sinDuplicados });
              }}
              renderTags={(value, getTagProps) =>
                value.map((correo, index) => {
                  const valido = EMAIL_REGEX.test(correo);
                  const { key, ...tagProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      variant="outlined"
                      size="small"
                      label={correo}
                      color={valido ? "default" : "error"}
                      {...tagProps}
                    />
                  );
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Correos de aviso"
                  placeholder={form.correos.length ? "Agregar otro..." : "ejemplo@trs.com.bo"}
                  required={form.correos.length === 0}
                  error={correosInvalidos.length > 0}
                  helperText={
                    correosInvalidos.length > 0
                      ? `Correo${correosInvalidos.length > 1 ? "s" : ""} inválido${correosInvalidos.length > 1 ? "s" : ""}: ${correosInvalidos.join(", ")}`
                      : "Escribe un correo y presiona Enter o coma para agregarlo."
                  }
                />
              )}
            />
            {isEdit && (
              <Typography variant="caption" color="text.secondary">
                Si cambias la fecha de vencimiento, los avisos ya enviados se reinician.
              </Typography>
            )}
          </Stack>
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button onClick={() => setOpenModal(false)}>Cancelar</Button>
            <Button type="submit" variant="contained" disabled={saving || !form.correos.length || correosInvalidos.length > 0}>
              {saving ? "Guardando..." : isEdit ? "Guardar cambios" : "Registrar"}
            </Button>
          </Stack>
        </Paper>
      </Modal>
    </DashboardLayout>
  );
};

export default VencimientoDocumentosPage;
